import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useEtablissement } from '@/contexts/EtablissementContext';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface ImportCSVDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CSVPreviewRow {
  prenom: string;
  nom: string;
  email: string;
  poste: string;
  type_contrat: string;
  date_embauche: string;
  valid: boolean;
  error?: string;
}

/**
 * Modal d'import CSV
 * Flow : télécharger template → upload fichier → preview → validation → import
 */
export const ImportCSVDialog = ({ open, onOpenChange }: ImportCSVDialogProps) => {
  const [preview, setPreview] = useState<CSVPreviewRow[]>([]);
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState({ created: 0, errors: 0 });
  const { etablissement } = useEtablissement();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    
    // Parser le CSV pour preview
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split('\n').filter(l => l.trim());
      
      // Skip header
      const rows: CSVPreviewRow[] = lines.slice(1).map(line => {
        const cols = line.split(/[,;]/).map(c => c.trim().replace(/^"|"$/g, ''));
        const [prenom, nom, email, poste, type_contrat, date_embauche] = cols;
        
        // Validation basique
        const validContrats = ['CDI', 'CDD', 'Benevole', 'Stage', 'Interim'];
        const emailValid = email && email.includes('@');
        const contratValid = validContrats.includes(type_contrat);
        
        return {
          prenom: prenom || '',
          nom: nom || '',
          email: email || '',
          poste: poste || '',
          type_contrat: type_contrat || '',
          date_embauche: date_embauche || '',
          valid: !!(prenom && nom && emailValid && contratValid),
          error: !prenom ? 'Prénom manquant' 
            : !nom ? 'Nom manquant' 
            : !emailValid ? 'Email invalide'
            : !contratValid ? 'Type contrat invalide'
            : undefined,
        };
      });
      
      setPreview(rows);
      setStep('preview');
    };
    reader.readAsText(selected);
  };

  const handleDownloadTemplate = () => {
    const template = 'prenom;nom;email;poste;type_contrat;date_embauche\nMarie;Dupont;marie@email.com;Éducatrice;CDI;2024-01-15\n';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_import_equipe.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!etablissement?.id) {
      toast({ title: 'Erreur', description: 'Établissement non trouvé', variant: 'destructive' });
      return;
    }

    setImporting(true);
    let created = 0;
    let errors = 0;

    const validRows = preview.filter(r => r.valid);

    // Insertion batch des membres valides
    for (const row of validRows) {
      try {
        const { data: membre, error } = await supabase
          .from('membres')
          .insert({
            etablissement_id: etablissement.id,
            prenom: row.prenom,
            nom: row.nom,
            email: row.email,
            poste: row.poste || null,
            type_contrat: row.type_contrat,
            date_embauche: row.date_embauche || null,
          })
          .select()
          .single();

        if (error) {
          console.error(`Erreur insertion ${row.prenom} ${row.nom}:`, error);
          errors++;
          continue;
        }

        // Créer une attestation en_attente pour chaque membre
        if (membre) {
          await supabase.from('attestations').insert({
            membre_id: membre.id,
            etablissement_id: etablissement.id,
            statut: 'en_attente',
          });

          // Envoyer l'email de demande d'attestation (non-bloquant)
          supabase.functions.invoke('send-attestation-request', {
            body: {
              membre_id: membre.id,
              email: row.email,
              prenom: row.prenom,
              nom: row.nom,
              etablissement_id: etablissement.id,
              type: 'demande_initiale',
            },
          }).catch(err => console.error('Erreur envoi email:', err));
        }

        created++;
      } catch (err) {
        console.error(`Erreur globale pour ${row.prenom} ${row.nom}:`, err);
        errors++;
      }
    }

    setImportResults({ created, errors });
    queryClient.invalidateQueries({ queryKey: ['membres'] });
    setStep('done');
    setImporting(false);
  };

  const validCount = preview.filter(r => r.valid).length;
  const errorCount = preview.filter(r => !r.valid).length;

  const handleClose = () => {
    setPreview([]);
    setStep('upload');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="app-content-centered-dialog max-w-[720px] rounded-[24px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] p-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <DialogHeader className="border-b border-[hsl(var(--app-border-subtle))] px-6 pb-5 pt-6">
          <DialogTitle className="text-[20px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))]">
            Import CSV
          </DialogTitle>
          <DialogDescription className="mt-1 text-[14px] leading-6 text-[hsl(var(--app-text-muted))]">
            Importez votre liste de membres depuis un fichier CSV.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5">
          {step === 'upload' && (
            <div className="space-y-4">
              {/* Télécharger le template */}
              <button
                onClick={handleDownloadTemplate}
                className="w-full rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] p-4 text-left transition-colors hover:bg-white"
              >
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Download className="h-5 w-5 text-[hsl(var(--app-text))]" />
                </div>
                <div>
                  <p className="text-[15px] font-medium text-[hsl(var(--app-text-strong))]">Télécharger le template CSV</p>
                  <p className="mt-0.5 text-[12px] leading-5 text-[hsl(var(--app-text-muted))]">
                    Colonnes : prénom, nom, email, poste, type_contrat, date_embauche
                  </p>
                </div>
              </button>

              {/* Zone upload */}
              <label className="flex h-36 w-full cursor-pointer flex-col items-center justify-center rounded-[18px] border border-dashed border-[hsl(var(--app-border-strong))] bg-[hsl(var(--app-surface-muted))] transition-colors hover:border-primary hover:bg-[hsl(var(--app-accent-soft))]">
                <Upload className="mb-2 h-8 w-8 text-[hsl(var(--app-text-muted))]" />
                <p className="text-[14px] text-[hsl(var(--app-text))]">
                  Glissez votre fichier CSV ici ou <span className="font-medium text-[hsl(var(--app-accent-strong))]">parcourir</span>
                </p>
                <p className="mt-1 text-[12px] text-[hsl(var(--app-text-muted))]">
                  Format CSV avec séparateur virgule ou point-virgule
                </p>
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              {/* Résumé */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="text-[hsl(var(--app-text))]">{validCount} valide{validCount > 1 ? 's' : ''}</span>
                </div>
                {errorCount > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 text-rose-500" />
                    <span className="text-rose-600">{errorCount} erreur{errorCount > 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>

              {/* Preview tableau */}
              <div className="max-h-64 overflow-y-auto rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-white">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[hsl(var(--app-surface-muted))]">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">Prénom</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">Nom</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">Email</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">Contrat</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--app-text-muted))]">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className={`border-t border-[hsl(var(--app-border-subtle))] ${!row.valid ? 'bg-rose-50/60' : ''}`}>
                        <td className="px-3 py-2 text-[13px] text-[hsl(var(--app-text))]">{row.prenom}</td>
                        <td className="px-3 py-2 text-[13px] text-[hsl(var(--app-text))]">{row.nom}</td>
                        <td className="max-w-[140px] truncate px-3 py-2 text-[13px] text-[hsl(var(--app-text))]">{row.email}</td>
                        <td className="px-3 py-2 text-[13px] text-[hsl(var(--app-text))]">{row.type_contrat}</td>
                        <td className="px-3 py-2">
                          {row.valid ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <span className="text-[12px] text-rose-600">{row.error}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
              <p className="text-lg font-medium text-[hsl(var(--app-text-strong))]">Import terminé</p>
              <p className="mt-1 text-sm text-[hsl(var(--app-text-muted))]">
                {importResults.created} membre{importResults.created > 1 ? 's' : ''} créé{importResults.created > 1 ? 's' : ''} · 
                Les demandes d'attestation ont été envoyées.
                {importResults.errors > 0 && ` · ${importResults.errors} erreur${importResults.errors > 1 ? 's' : ''}`}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-[hsl(var(--app-border-subtle))] px-6 py-4">
          {step === 'upload' && (
            <Button variant="outline" onClick={handleClose} className="rounded-[14px] border-[hsl(var(--app-border-subtle))]">Annuler</Button>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => { setStep('upload'); setPreview([]); }} className="rounded-[14px] border-[hsl(var(--app-border-subtle))]">
                Retour
              </Button>
              <Button
                onClick={handleImport}
                disabled={validCount === 0 || importing}
                className="rounded-[14px]"
              >
                {importing ? 'Import en cours...' : `Importer ${validCount} membre${validCount > 1 ? 's' : ''}`}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} className="rounded-[14px]">
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
